import { TimelineItem } from '@/features/timeline';
import { ItemPosition, TimelineItemWithDisplay } from '../../models/timeline-calendar.types';
import { PERIOD_ITEM_COLOR, POINT_ITEM_COLOR } from '../../libs/const';
import {
  PENDING_ITEM_ID,
  POINT_LABEL_MAX_WIDTH_PX,
  ROW_HEIGHT_PX,
} from './constants';
import { isPointItem, partitionBaseItems } from './layout-utils';
import { CreateDraft, MovePreviewState, ResizePreviewState } from './types';
import { useMemo } from 'react';

interface UseTimelineCalendarLayoutParams {
  timelines: Array<{ id: string; label: string }>;
  itemsWithColors: TimelineItemWithDisplay[];
  calculateItemPosition: (item: TimelineItem) => ItemPosition;
  shouldShowText: (width: string) => boolean;
  pendingCreateDraft: CreateDraft | null;
  pendingCreateTitle: string;
  movePreviewState: MovePreviewState | null;
  resizePreviewState: ResizePreviewState | null;
  dragPreviewDraft: CreateDraft | null;
  hoverPreviewDraft: CreateDraft | null;
  addMode: 'none' | 'duration' | 'milestone';
  previewClientY: number | null;
  timelineItemsRef: React.RefObject<HTMLDivElement>;
  sectionById: Map<string, { sectionIndex: number }>;
  hiddenSectionIds?: Set<string>;
}

export const useTimelineCalendarLayout = ({
  timelines,
  itemsWithColors,
  calculateItemPosition,
  shouldShowText,
  pendingCreateDraft,
  pendingCreateTitle,
  movePreviewState,
  resizePreviewState,
  dragPreviewDraft,
  hoverPreviewDraft,
  addMode,
  previewClientY,
  timelineItemsRef,
  sectionById,
  hiddenSectionIds = new Set<string>(),
}: UseTimelineCalendarLayoutParams) => {
  const fallbackSectionId = timelines[0]?.id ?? '';

  const pendingCreateDisplayItem = useMemo(() => {
    if (!pendingCreateDraft) {
      return null;
    }

    return {
      id: PENDING_ITEM_ID,
      title: pendingCreateTitle,
      sectionId: pendingCreateDraft.sectionId ?? timelines[0]?.id ?? '',
      sectionLabel: '',
      sectionIndex: Math.max(0, timelines.findIndex(( section) => section.id === pendingCreateDraft.sectionId)),
      startDate: pendingCreateDraft.startDate,
      endDate: pendingCreateDraft.endDate,
      color: pendingCreateDraft.endDate ? PERIOD_ITEM_COLOR : POINT_ITEM_COLOR,
      index: itemsWithColors.length,
    } as TimelineItemWithDisplay;
  }, [timelines, itemsWithColors.length, pendingCreateDraft, pendingCreateTitle]);

  const renderItems = useMemo(() => {
    const source = pendingCreateDisplayItem
      ? [...itemsWithColors, pendingCreateDisplayItem]
      : [...itemsWithColors];

    return source.sort((a, b) => (
      Date.parse(a.startDate) - Date.parse(b.startDate)
    ));
  }, [itemsWithColors, pendingCreateDisplayItem]);

  const displayItems = useMemo(() => {
    const movedItems = !movePreviewState
      ? renderItems
      : renderItems.map((item) => (
        item.id === movePreviewState.itemId
          ? {
            ...item,
            sectionId: movePreviewState.sectionId,
            sectionIndex: sectionById.get(movePreviewState.sectionId)?.sectionIndex ?? 0,
            startDate: movePreviewState.draft.startDate,
            endDate: movePreviewState.draft.endDate,
          }
          : item
      ));

    if (!resizePreviewState) {
      return movedItems;
    }

    return movedItems.map((item) => (
      item.id === resizePreviewState.itemId
        ? {
          ...item,
          sectionId: resizePreviewState.sectionId,
          sectionIndex: sectionById.get(resizePreviewState.sectionId)?.sectionIndex ?? 0,
          startDate: resizePreviewState.draft.startDate,
          endDate: resizePreviewState.draft.endDate,
        }
        : item
    ));
  }, [sectionById, movePreviewState, renderItems, resizePreviewState]);

  const positionedRenderItems = useMemo(
    () => displayItems.map((item) => ({ item, position: calculateItemPosition(item) })),
    [displayItems, calculateItemPosition],
  );
  const positionedBaseItems = useMemo(
    () => itemsWithColors.map((item) => ({ item, position: calculateItemPosition(item) })),
    [itemsWithColors, calculateItemPosition],
  );

  const isMultiSection = timelines.length > 1;

  const baseItemPartitions = useMemo(
    () => partitionBaseItems(positionedBaseItems),
    [positionedBaseItems],
  );

  const baseVisibleItemCount = baseItemPartitions.baseVisibleItemCount;

  const singleSectionRowCount = baseVisibleItemCount;

  const sectionVisibleRowsById = useMemo(() => {
    const counts = new Map<string, number>();
    positionedRenderItems.forEach(({ item, position }) => {
      if (!position.isVisible) {
        return;
      }
      const sectionId = item.sectionId ?? fallbackSectionId;
      counts.set(sectionId, (counts.get(sectionId) ?? 0) + 1);
    });
    return counts;
  }, [fallbackSectionId, positionedRenderItems]);

  const multiSectionLayout = useMemo(() => {
    const sectionOffsetRows = new Map<string, number>();
    const sectionRows = new Map<string, number>();
    let runningRows = 0;
    timelines.forEach((section, index) => {
      const isHidden = hiddenSectionIds.has(section.id);
      const visibleRows = isHidden ? 0 : (sectionVisibleRowsById.get(section.id) ?? 0);
      const rows = isHidden ? 0 : Math.max(1, visibleRows);
      sectionOffsetRows.set(section.id, runningRows);
      sectionRows.set(section.id, rows);
      const isLastSection = index === timelines.length - 1;
      runningRows += rows + (isLastSection ? 0 : 1);
    });
    return {
      sectionOffsetRows,
      sectionRows,
      totalRows: runningRows,
    };
  }, [timelines, sectionVisibleRowsById, hiddenSectionIds]);

  const activePreviewDraft = dragPreviewDraft ?? hoverPreviewDraft;
  const previewItem: TimelineItem | null = activePreviewDraft
    ? {
      id: '__preview__',
      title: '',
      startDate: activePreviewDraft.startDate,
      endDate: activePreviewDraft.endDate,
    }
    : null;

  const previewPosition = previewItem ? calculateItemPosition(previewItem) : null;
  const shouldShowPreview = Boolean(previewItem && previewPosition?.isVisible && !pendingCreateDraft);
  const previewIsPoint = previewItem ? isPointItem(previewItem) : false;
  const isDurationCursorPreview = Boolean(!dragPreviewDraft && hoverPreviewDraft && addMode === 'duration');
  const renderPreviewAsPoint = previewIsPoint && !isDurationCursorPreview;

  const getPreviewTop = () => {
    const previewHeight = renderPreviewAsPoint ? 12 : 36;
    const pointerOffsetY = renderPreviewAsPoint ? 6 : 10;
    const defaultSectionId = pendingCreateDraft?.sectionId ?? timelines[0]?.id ?? '';
    const sectionOffsetRow = isMultiSection
      ? (multiSectionLayout.sectionOffsetRows.get(defaultSectionId) ?? 1)
      : singleSectionRowCount;
    const fallbackTop = sectionOffsetRow * ROW_HEIGHT_PX + 10;
    if (previewClientY === null || !timelineItemsRef.current) {
      return fallbackTop;
    }

    const rect = timelineItemsRef.current.getBoundingClientRect();
    const rawTop = previewClientY - rect.top + pointerOffsetY;
    const maxTop = Math.max(rect.height - previewHeight, 0);
    return Math.min(Math.max(rawTop, 0), maxTop);
  };

  const previewTop = getPreviewTop();

  const timelineRows = isMultiSection
    ? multiSectionLayout.totalRows + (pendingCreateDraft || shouldShowPreview ? 1 : 0)
    : singleSectionRowCount + (pendingCreateDraft || shouldShowPreview ? 1 : 0);
  const timelineMinHeight = `${timelineRows * ROW_HEIGHT_PX + 50}px`;

  return {
    isMultiSection,
    pendingCreateDisplayItem,
    positionedRenderItems,
    multiSectionLayout,
    timelineMinHeight,
    shouldShowPreview,
    previewPosition,
    previewTop,
    renderPreviewAsPoint,
    isDurationCursorPreview,
    shouldShowText,
    PENDING_ITEM_ID,
    POINT_LABEL_MAX_WIDTH_PX,
  };
};
